import type { ScopeSectionSchema } from './types'

export const recipesSections: ScopeSectionSchema[] = [
  {
    id: 'recipes_core',
    tabId: 'recipes',
    formTitle: 'Workflow Builder',
    formDescription: 'Define ordered checks and associate IDV flows for different customer journeys',
    pdfTitle: 'Recipes/Workflows',
    pdfDescription: 'Verification workflows and ordered checks.',
    highLevelSummary: 'Workflows and ordered checks configuration.',
    groups: [
      {
        id: 'recipes_tab_header',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        formTitle: 'Workflow Builder',
        formDescription: 'Define ordered checks and associate IDV flows for different customer journeys',
        pdfTitle: 'Workflow Builder',
        pdfDescription: 'A workflow is a series of checks. The order of the checks can be changed. Within a check, you can choose options and data sources. FrankieOne will work with you to construct workflows that work for you. Risk influences a workflow.',
      },
      {
        id: 'recipes_inherited_aml_group',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        formTitle: 'Inherited Flows, Rulesets and AML',
        formDescription: 'Settings inherited from other tabs: IDV flows from Biometrics tab, KYC rulesets from KYC tab, Credit Header settings from KYC tab, and AML settings from AML tab.',
        pdfTitle: 'Inherited Flows, Rulesets and AML',
        pdfDescription: 'Settings inherited from other tabs for use in workflows.',
      },
      {
        id: 'recipes_workflows_group',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        formTitle: 'Workflows',
        pdfTitle: 'Workflows',
        pdfDescription: 'Workflows list and configuration.',
      },
      {
        id: 'recipes_preset_workflows_group',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        formTitle: 'Preset workflows',
        formDescription: 'Quickly add a preset workflow template.',
        pdfTitle: 'Preset workflows',
        pdfDescription: 'Preset workflow templates.',
      },
      {
        id: 'recipes_preset_dropdown_group',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        formTitle: 'Add preset…',
        helperText: 'Add preset…',
        pdfTitle: 'Preset dropdown',
        pdfDescription: 'Preset workflow dropdown label.',
      },
      {
        id: 'recipes_empty_state_group',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        formTitle: 'No workflows yet. Click “Add Workflow” to create one.',
        pdfTitle: 'Empty state',
        pdfDescription: 'No workflows yet empty state.',
      },
      {
        id: 'recipes_workflow_fields_group',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        formTitle: 'Workflow fields',
        pdfTitle: 'Workflow fields',
        pdfDescription: 'Common workflow field labels/placeholders.',
      },
    ],
    fields: [
      {
        id: 'recipes_preset_dropdown',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_preset_dropdown_group',
        path: 'workflowsConfig',
        valueType: 'select',
        formLabel: 'Add preset…',
        formDescription: 'Quickly add a preset workflow template.',
        tooltip: 'Preset workflows provide pre-configured verification sequences for common use cases. "Full" includes comprehensive checks with 2x2 document verification, "Step-up" provides progressive verification paths, and "Simple" offers streamlined 1x1 document verification for lower-risk scenarios.',
        pdfLabel: 'Add preset',
        pdfDescription: 'Preset workflow dropdown placeholder.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_inherited_aml_summary',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_inherited_aml_group',
        path: 'amlConfig.level',
        valueType: 'text',
        formLabel: 'Inherited AML settings',
        formDescription: 'AML settings are taken from the AML tab and applied to AML steps in workflows.',
        tooltip: 'This section displays configuration inherited from other tabs that workflows can reference: IDV flows from Biometrics tab define the customer verification experience, KYC rulesets from KYC tab control document requirements, Credit Header settings determine credit bureau data access, and AML settings define screening requirements.',
        pdfLabel: 'Inherited AML settings',
        pdfDescription: 'AML settings used by workflow AML steps.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_workflow_name',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'Workflow name',
        tooltip: 'A descriptive name for this workflow that identifies its purpose or target use case. Examples: "Standard Individual Onboarding", "High-Risk Customer Verification", "Step-up Authentication". This name is used for internal reference and workflow selection.',
        pdfLabel: 'Workflow name',
        pdfDescription: 'Workflow name.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_workflow_id',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'Workflow ID',
        tooltip: 'The unique technical identifier used to reference this workflow in API calls and system integrations. This ID must be provided when initiating verification requests to execute this specific workflow. Typically uses snake_case format (e.g., "standard_onboarding", "high_risk_kyc").',
        pdfLabel: 'Workflow ID',
        pdfDescription: 'Workflow identifier.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_workflow_url',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'Workflow URL',
        placeholder: 'https://...',
        tooltip: 'The FrankieOne platform URL where this workflow is configured and can be viewed or edited. This typically links to the workflow definition in the FrankieOne Portal, allowing stakeholders to review the detailed configuration and access workflow analytics.',
        pdfLabel: 'Workflow URL',
        pdfDescription: 'Workflow URL.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_workflow_notes',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'textarea',
        formLabel: 'Notes',
        placeholder: 'Capture the detailed workflow logic here.',
        tooltip: 'Document the business logic, decision rules, and special considerations for this workflow. Include details like when this workflow should be used, which customer segments it targets, any conditional branching logic, edge cases, exceptions, and integration requirements with other systems.',
        pdfLabel: 'Workflow notes',
        pdfDescription: 'Workflow notes.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_associated_flow',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'select',
        formLabel: 'Associated Flow (optional)',
        formDescription: 'Select an IDV flow to associate with this workflow (optional).',
        tooltip: 'Link an IDV flow (configured in the Biometrics tab) to this workflow to define the customer-facing verification experience. The associated flow determines which screens are shown (consent, document capture, biometrics, review) and which verification vendor is used. Leave blank to use the default flow.',
        pdfLabel: 'Associated flow',
        pdfDescription: 'Associated IDV flow.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_ordered_checks',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'select',
        formLabel: 'Ordered checks',
        tooltip: 'Define the sequence of verification checks performed in this workflow. Checks execute in order from top to bottom, with each check\'s result potentially affecting whether subsequent checks run. Available checks include: KYC (document verification with rulesets), IDV (identity verification flow), AML (screening), Credit Headers, and KYB (business verification).',
        pdfLabel: 'Ordered checks',
        pdfDescription: 'Ordered checks.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_add_check',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'select',
        formLabel: 'Add check…',
        tooltip: 'Add a new verification check to the workflow sequence. Choose from available check types based on your configuration: KYC checks verify documents using selected rulesets, IDV checks use the associated flow for identity verification, AML checks screen against sanctions and watchlists, Credit Headers pull bureau data, and KYB checks verify business entities.',
        pdfLabel: 'Add check',
        pdfDescription: 'Add check dropdown placeholder.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_use_default_flow',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'Use default flow',
        pdfLabel: 'Use default flow',
        pdfDescription: 'Associated flow default option label.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_no_checks_yet',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'No checks yet. Use “Add check…” to add steps.',
        pdfLabel: 'No checks yet',
        pdfDescription: 'Empty steps message.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_kyc_ruleset',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'select',
        formLabel: 'Ruleset',
        placeholder: 'Select ruleset',
        pdfLabel: 'Ruleset',
        pdfDescription: 'Ruleset selection label/placeholder.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_gov_id_ruleset_helper',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'Gov ID check is present, so “no Gov ID” rulesets are disabled.',
        pdfLabel: 'Gov ID ruleset helper',
        pdfDescription: 'Helper text when Gov ID step is present.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_kyc_cascade',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'select',
        formLabel: 'Cascade (ordered)',
        placeholder: 'Add source…',
        pdfLabel: 'Cascade (ordered)',
        pdfDescription: 'Cascade selection label/placeholder.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_idv_requirement',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'select',
        formLabel: 'Requirement',
        pdfLabel: 'Requirement',
        pdfDescription: 'IDV step requirement label.',
        contributesToCompletion: false,
        options: [
          { value: 'mandatory', label: 'Mandatory' },
          { value: 'optional', label: 'Optional' },
        ],
      },
      {
        id: 'recipes_idv_flow_override',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'select',
        formLabel: 'Flow override (optional)',
        pdfLabel: 'Flow override',
        pdfDescription: 'IDV step flow override label.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_idv_use_workflow_default_flow',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'Use workflow/default flow',
        pdfLabel: 'Use workflow/default flow',
        pdfDescription: 'IDV step default flow override option label.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_idv_flow_details',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'Flow details',
        pdfLabel: 'Flow details',
        pdfDescription: 'IDV step flow details label.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_risk_branches',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'Branches',
        pdfLabel: 'Branches',
        pdfDescription: 'Risk step branches header.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_risk_add_branch',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'Add branch',
        pdfLabel: 'Add branch',
        pdfDescription: 'Risk step add branch button label.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_risk_branches_empty',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'Add a branch to capture International cascade changes or step-up checks (AML/Fraud/Gov ID/KYC).',
        pdfLabel: 'Branches empty',
        pdfDescription: 'Risk step branches empty state.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_risk_branch_condition',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'Condition / trigger',
        placeholder: 'e.g. doc_country != AU OR risk_score > threshold',
        pdfLabel: 'Condition / trigger',
        pdfDescription: 'Risk branch condition label/placeholder.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_risk_branch_outcome',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'select',
        formLabel: 'Outcome',
        formDescription: 'Set the expected outcome for this branch (Pass, Fail, or In Review).',
        tooltip: 'Optionally assign an outcome to this risk branch. This indicates the expected verification result when this branch\'s condition is met: Pass (approved), Fail (rejected), or In Review (requires manual review). Leave unset if the branch only runs additional checks.',
        pdfLabel: 'Branch outcome',
        pdfDescription: 'Risk branch expected outcome.',
        contributesToCompletion: false,
        options: [
          { value: '', label: 'Not set' },
          { value: 'pass', label: 'Pass' },
          { value: 'fail', label: 'Fail' },
          { value: 'in_review', label: 'In Review' },
        ],
      },
      {
        id: 'recipes_risk_branch_checks',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'Checks to run for this branch',
        pdfLabel: 'Branch checks',
        pdfDescription: 'Risk branch checks label.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_risk_branch_add_check',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'Add check…',
        pdfLabel: 'Add check',
        pdfDescription: 'Risk branch add check placeholder.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_risk_branch_no_checks',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'No checks yet for this branch.',
        pdfLabel: 'No checks yet for this branch',
        pdfDescription: 'Risk branch empty checks message.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_duplicate_check_notes',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        groupId: 'recipes_workflows_group',
        path: 'workflowsConfig',
        valueType: 'textarea',
        formLabel: 'Duplicate check notes',
        formDescription: 'Describe what this duplicate check will entail.',
        tooltip: 'Document the matching criteria and rules for this duplicate check. Include details like which fields to match on (e.g., full name + DOB), matching strategy (exact vs fuzzy), entity types to compare against, and any special handling for edge cases.',
        placeholder: 'e.g. Match on full name + DOB across all entity types...',
        pdfLabel: 'Duplicate check notes',
        pdfDescription: 'Notes describing the duplicate check matching criteria.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_number_of_workflows_summary',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'Number of workflows (summary)',
        pdfLabel: 'Number of workflows',
        pdfDescription: 'Add at least one workflow to define check order and requirements.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_workflow_builder_summary',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'Workflow (builder) summary (PDF)',
        pdfLabel: 'Workflow',
        pdfDescription: 'Add a workflow name and at least one check step.',
        contributesToCompletion: false,
      },
      {
        id: 'recipes_workflow_complex_summary',
        tabId: 'recipes',
        sectionId: 'recipes_core',
        path: 'workflowsConfig',
        valueType: 'text',
        formLabel: 'Workflow (complex) summary (PDF)',
        pdfLabel: 'Workflow',
        pdfDescription: 'Add a workflow name, workflow ID, and URL.',
        contributesToCompletion: false,
      },
    ],
  },
]
